import { Component } from '@theme/component';

const MINUTE = 60 * 1000;

/**
 * Shows "order within X — ships Y" using the shopper's clock. The message stays hidden until
 * this component connects, so no stale or server-cached time is ever displayed.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} message - Element that receives the formatted message.
 *
 * @extends Component<Refs>
 */
class CpShipCutoffComponent extends Component {
  requiredRefs = ['message'];

  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    super.connectedCallback();
    this.#render();
    this.#timer = window.setInterval(() => this.#render(), MINUTE);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.clearInterval(this.#timer);
  }

  #render() {
    const { message } = this.refs;
    const template = this.dataset.template ?? '';
    const cutoffHour = Number(this.dataset.cutoffHour ?? 15);
    const processingDays = Number(this.dataset.processingDays ?? 2);
    const skipWeekends = this.dataset.skipWeekends === 'true';

    const now = new Date();
    const cutoff = new Date(now);
    cutoff.setHours(cutoffHour, 0, 0, 0);
    if (now >= cutoff) cutoff.setDate(cutoff.getDate() + 1);

    const minutes = Math.max(0, Math.round((cutoff.getTime() - now.getTime()) / MINUTE));
    const ship = new Date(cutoff);
    ship.setDate(ship.getDate() + processingDays);
    while (skipWeekends && (ship.getDay() === 0 || ship.getDay() === 6)) {
      ship.setDate(ship.getDate() + 1);
    }

    const hours = Math.floor(minutes / 60);
    const time = `${hours}h ${minutes % 60}m`;
    const date = ship.toLocaleDateString(document.documentElement.lang || undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });

    /** @type {Record<string, string>} */
    const values = { '[time]': time, '[date]': date };
    const nodes = template.split(/(\[time\]|\[date\])/).map((part) => {
      const value = values[part];
      if (!value) return document.createTextNode(part);

      const strong = document.createElement('strong');
      strong.textContent = value;
      return strong;
    });

    message.replaceChildren(...nodes);
    message.hidden = false;
  }
}

if (!customElements.get('cp-ship-cutoff-component')) {
  customElements.define('cp-ship-cutoff-component', CpShipCutoffComponent);
}
